import type {Property} from "@/src/modules/properties/types";

import type {Me} from "../types";

const DISPATCH_CENTER_MANDATORY_SECTIONS = ["properties", "users"] as const;

/** Always hidden for dispatch + dispatch supervisor (nav, routes, quick actions). */
const DISPATCH_OPERATOR_HIDDEN_SECTIONS = ["citations", "safelistings"] as const;

/**
 * User-scoped routes (header / account comms). Gated by RBAC only — not dispatch-center
 * `enabled_sections` (those keys are property modules like `dars`, `dispatchlogs`).
 */
const DISPATCH_OPERATOR_USER_SCOPED_ROUTE_KEYS = ["directmessages"] as const;

const PERMISSION_RESOURCE_TO_SECTION: Record<string, string> = {
  property: "properties",
  user: "users",
  dar: "dars",
  shift: "shifts",
  dispatchlog: "dispatchlogs",
  household: "households",
  incidentreport: "incidents",
  citation: "citations",
  briefingboard: "briefings",
  safelisting: "safelistings",
  vehicle: "vehicles",
  dispatchcenter: "dispatchcenters",
};

export function isDispatchOperatorRole(me: Me | null | undefined): boolean {
  if (!me) return false;
  return me.isDispatch || me.isDispatchSupervisor;
}

/** When false, dispatch users land on /dispatchdesk and skip personal shift UI. */
export function dispatchCenterRequiresShift(me: Me | null | undefined): boolean {
  if (!me) return true;
  if (!isDispatchOperatorRole(me)) return true;
  if (!me.dispatch_center) return true;
  return me.dispatch_center.requires_shift !== false;
}

/** Post-login and primary fallback route for dispatch operators. */
export function getDispatchPostLoginRoute(me: Me | null | undefined): "/home" | "/dispatchdesk" {
  return dispatchCenterRequiresShift(me) ? "/home" : "/dispatchdesk";
}

/** Nav / guard fallback when a dispatch operator hits a blocked route. */
export function getDispatchOperatorFallbackRoute(
  me: Me | null | undefined,
): "/home" | "/dispatchdesk" {
  return getDispatchPostLoginRoute(me);
}

/** Personal shift chip on dispatch desk (centers that require shifts). */
export function showDispatchPersonalShiftBlock(me: Me | null | undefined): boolean {
  if (!isDispatchOperatorRole(me)) return false;
  return dispatchCenterRequiresShift(me);
}

/** Center policy section keys for dispatch roles; null for everyone else. */
export function getDispatchCenterEnabledSections(me: Me | null | undefined): string[] | null {
  if (!isDispatchOperatorRole(me)) return null;
  const sections = me?.dispatch_center?.enabled_sections;
  if (!sections?.length) return null;
  return sections;
}

/**
 * Whether a feature section is enabled for this dispatch user's center.
 * Non-dispatch roles always pass (property-level gating applies elsewhere).
 */
export function isDispatchCenterSectionEnabled(
  me: Me | null | undefined,
  sectionKey: string,
): boolean {
  if (!isDispatchOperatorRole(me)) return true;

  if ((DISPATCH_OPERATOR_HIDDEN_SECTIONS as readonly string[]).includes(sectionKey)) {
    return false;
  }

  const enabled = getDispatchCenterEnabledSections(me);
  if (!enabled) return true;

  if ((DISPATCH_CENTER_MANDATORY_SECTIONS as readonly string[]).includes(sectionKey)) {
    return true;
  }

  return enabled.includes(sectionKey);
}

/** Gate by permission resource (e.g. `dar`) using center section policy. */
export function isDispatchCenterResourceEnabled(
  me: Me | null | undefined,
  permissionResource: string,
): boolean {
  const sectionKey = PERMISSION_RESOURCE_TO_SECTION[permissionResource];
  if (!sectionKey) return true;
  return isDispatchCenterSectionEnabled(me, sectionKey);
}

/** Nav / route key may differ from section key (e.g. parking-enforcement → vehicles). */
export function isDispatchCenterRouteEnabled(
  me: Me | null | undefined,
  routeKey: string,
): boolean {
  if (
    (DISPATCH_OPERATOR_USER_SCOPED_ROUTE_KEYS as readonly string[]).includes(routeKey)
  ) {
    return true;
  }
  if (routeKey === "dispatchdesk") {
    return isDispatchOperatorRole(me);
  }
  if (routeKey === "home") {
    return isDispatchOperatorRole(me) && dispatchCenterRequiresShift(me);
  }
  return isDispatchCenterSectionEnabled(me, routeKey);
}

/** Dispatch center anchor property from `/me` — do not use `GET /properties?filter[is_dispatch]=1` for dispatch roles. */
export function getDispatchCenterAnchorProperty(
  me: Me | null | undefined,
): Pick<Property, "id" | "name"> | null {
  if (!isDispatchOperatorRole(me)) return null;
  const anchor = me?.dispatch_center?.property?.[0];
  if (!anchor?.id) return null;
  return {id: anchor.id, name: anchor.name};
}
